import {
  Box,
  Checkbox,
  Icon,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  withStyles,
} from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import Check from "@material-ui/icons/Check";
import Close from "@material-ui/icons/Close";
import Launch from "@material-ui/icons/Launch";
import * as Sentry from "@sentry/react";
import { format, parseISO } from "date-fns";
import React, { Dispatch, SetStateAction, useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { setParameters } from "../../../../actions";
import DeleteIcon from "../../../../assets/images/trash_torch_red.svg";
import { ErrorMsg } from "../../../../components/AppMessages";
import { DARK_ACCENT, DASHBOARD_URLS, TORCH_RED, WHITE } from "../../../../constants";
import { Brand, Channel, Market, Metric, Model, Order, Region, Tag } from "../../../../constants/interface/admin/common";
import { ThemeContext } from "../../../../context";
import { convertParamsToQueryParams } from "../../../../utils/annotationFunctions";
import { getComparator, stableSort } from "../../utils";

interface Props {
  annotations: any[];
  selectedAnnotation: any;
  setSelectedAnnotation: Dispatch<SetStateAction<any>>;
  setDeleteDialogOpen: Dispatch<SetStateAction<boolean>>;
}

// Zebra-stripe color made theme-aware - see UsersTable.tsx's own copy of this same fix for the
// full reasoning (same bug, same pattern, repeated across every Admin table file).
const getStyledTableRow = (oddRowBackground: string) =>
  withStyles(() => ({
    root: {
      "&:nth-of-type(odd)": {
        backgroundColor: oddRowBackground,
      },
      "&:hover": {
        backgroundColor: "rgba(244, 2, 42, 0.15)",
      },
    },
  }))(TableRow);

const DarkStyledTableRow = getStyledTableRow("rgba(225, 225, 225, 0.15)");
const LightStyledTableRow = getStyledTableRow(`${DARK_ACCENT}1a`);

const WhiteBackgroundCheckbox = withStyles(() => ({
  root: {
    color: "black",
    "& .MuiIconButton-label": {
      position: "relative",
      zIndex: 0,
    },
    "&:not($checked) .MuiIconButton-label": {
      color: WHITE,
    },
    "&$checked .MuiIconButton-label": {
      color: TORCH_RED,
    },
    "&:not($checked) .MuiIconButton-label:after": {
      content: '""',
      left: 4,
      top: 4,
      height: 15,
      width: 15,
      position: "absolute",
      backgroundColor: WHITE,
      zIndex: -1,
    },
    "& .MuiIconButton-colorSecondary": {
      color: `${TORCH_RED} !important`,
    },
  },
  checked: {},
}))(Checkbox);

// Defined once at module scope (rather than calling makeStyles({...})() inline on every render)
// - the inline pattern creates a brand new style-sheet hook per render, which under React 18
// crashes on unmount with "Cannot read properties of undefined (reading 'refs')" in
// @material-ui/styles' detach cleanup.
//
// Two separate static hooks (one per theme) rather than one hook with textColor as a dynamic
// "function of props" rule: this table's background now follows the app's light/dark theme
// (.admin_tile uses themed("primary-tile-background")), and a props-based dynamic color here
// intermittently rendered dark-theme text in DARK_ACCENT (a near-black navy that happens to
// exactly equal the dark theme's own background grey) instead of WHITE - both hooks below are
// always called (Rules of Hooks), and the component picks whichever one matches the current
// theme, so there's no dynamic-value JSS behavior involved at all.
const sharedCellStyles = {
  noBorder: {
    border: "none",
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  deleteIcon: {
    width: "1.5rem",
    cursor: "pointer",
    "&:hover": {
      filter: "brightness(0.85)",
    },
  },
  launchIcon: {
    fill: TORCH_RED,
  },
} as const;

const useDarkStyles = makeStyles({
  ...sharedCellStyles,
  tableCell: { color: WHITE, border: "none", padding: "10px !important" },
  tableHeadCell: { color: WHITE, border: "none", fontWeight: 600, padding: "10px !important" },
  orderIcon: { color: `${WHITE} !important`, width: "2.5rem", height: "2.5rem" },
  tablePagination: {
    color: WHITE,
    width: "auto",
    "& .MuiTablePagination-spacer": {
      flex: "none",
    },
  },
});

const useLightStyles = makeStyles({
  ...sharedCellStyles,
  tableCell: { color: DARK_ACCENT, border: "none", padding: "10px !important" },
  tableHeadCell: { color: DARK_ACCENT, border: "none", fontWeight: 600, padding: "10px !important" },
  orderIcon: { color: `${DARK_ACCENT} !important`, width: "2.5rem", height: "2.5rem" },
  tablePagination: {
    color: DARK_ACCENT,
    width: "auto",
    "& .MuiTablePagination-spacer": {
      flex: "none",
    },
  },
});

const AnnotationsTable = (props: Props) => {
  const { annotations, selectedAnnotation, setSelectedAnnotation, setDeleteDialogOpen } = props;
  const [rowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>("id");

  const dispatch = useDispatch();

  const history = useHistory();

  const handleRequestSort = (event: any, property: any) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const createSortHandler = (property: any) => (event: any) => {
    handleRequestSort(event, property);
  };

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  const headCells = [
    { id: "owner", numeric: false, disablePadding: false, label: "Owner", sortable: true },
    { id: "created_at", numeric: false, disablePadding: false, label: "Creation Date", sortable: true },
    { id: "approval_status", numeric: false, disablePadding: false, label: "Status", sortable: true },
    { id: "dashboard", numeric: false, disablePadding: false, label: "Dashboard", sortable: true },
    { id: "start_date", numeric: false, disablePadding: false, label: "Date", sortable: true },
    { id: "brands", numeric: false, disablePadding: false, label: "Brands", sortable: true },
    { id: "regions", numeric: false, disablePadding: false, label: "Regions", sortable: true },
    { id: "markets", numeric: false, disablePadding: false, label: "Markets", sortable: true },
    { id: "models", numeric: false, disablePadding: false, label: "Models", sortable: true },
    { id: "channels", numeric: false, disablePadding: false, label: "Channels", sortable: true },
    { id: "metrics", numeric: false, disablePadding: false, label: "Metrics", sortable: true },
    { id: "tags", numeric: false, disablePadding: false, label: "Tags", sortable: true },
    { id: "scope", numeric: false, disablePadding: false, label: "Scope", sortable: true },
    { id: "insight", numeric: false, disablePadding: false, label: "Insight", sortable: true },
  ];

  const { theme } = useContext(ThemeContext);
  const textColor = theme === "dark" ? WHITE : DARK_ACCENT;
  const darkClasses = useDarkStyles();
  const lightClasses = useLightStyles();
  const classes = theme === "dark" ? darkClasses : lightClasses;
  const StyledTableRow = theme === "dark" ? DarkStyledTableRow : LightStyledTableRow;

  return (
    <Sentry.ErrorBoundary fallback={<ErrorMsg />}>
      <Box>
        <TableContainer component={Box}>
          <Table className={`${classes.noBorder}`}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCell} style={{ width: "50px" }}></TableCell>
                {headCells.map((headCell, index) => (
                  <TableCell
                    key={index}
                    className={classes.tableHeadCell}
                    align={headCell.numeric ? "right" : "left"}
                    padding={headCell.disablePadding ? "none" : "normal"}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        style={{ color: textColor, fontWeight: 600 }}
                        classes={{
                          icon: classes.orderIcon,
                        }}
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : "asc"}
                        onClick={createSortHandler(headCell.id)}
                        IconComponent={ArrowDropDownIcon}
                      >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          <span className={classes.visuallyHidden}>{order === "desc" ? "sorted descending" : "sorted ascending"}</span>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(annotations, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((annotation: any, index) => (
                  <StyledTableRow key={index}>
                    <TableCell className={`${classes.tableCell}`}>
                      <WhiteBackgroundCheckbox
                        checked={selectedAnnotation?.id == annotation.id}
                        onChange={(e) => {
                          e.target.checked ? setSelectedAnnotation(annotation) : setSelectedAnnotation(undefined);
                        }}
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                      />
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`}>{annotation.user.email}</TableCell>
                    <TableCell className={`${classes.tableCell}`}>{format(parseISO(annotation.created_at), "y-LL-dd")}</TableCell>
                    <TableCell className={`${classes.tableCell}`}>
                      {annotation.approval_status == "unapproved" ? (
                        <Tooltip title="Not approved">
                          <Close style={{ fill: "red" }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title={annotation.approval_status == "approved" ? "Approved" : "Pending"}>
                          <Check style={{ fill: annotation.approval_status == "approved" ? "green" : "grey" }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`}>{annotation.dashboard}</TableCell>
                    <TableCell className={`${classes.tableCell}`}>
                      {annotation.start_date} - {annotation.end_date}
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`}>
                      {Array.isArray(annotation.brands) && annotation.brands.map((brand: Brand) => brand.brand).join(", ")}
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`}>
                      {Array.isArray(annotation.regions) && annotation.regions.map((region: Region) => region.region).join(", ")}
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`}>
                      {Array.isArray(annotation.markets) && annotation.markets.map((market: Market) => market.market).join(", ")}
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`}>
                      {Array.isArray(annotation.models) && annotation.models.map((model: Model) => model.model).join(", ")}
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`}>
                      {Array.isArray(annotation.channels) && annotation.channels.map((channel: Channel) => channel.channel).join(", ")}
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`}>
                      {Array.isArray(annotation.metrics) && annotation.metrics.map((metric: Metric) => metric.metric).join(", ")}
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`}>
                      {Array.isArray(annotation.tags) && annotation.tags.map((tag: Tag) => tag.tag).join(", ")}
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`}>
                      {annotation.applies_to == "filters_only" ? "these tags only" : "these tags and levels above"}
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`}>{annotation.insight}</TableCell>
                    <TableCell className={`${classes.tableCell}`}>
                      <Icon
                        id="launch-btn"
                        className={classes.launchIcon}
                        component={Launch}
                        onClick={() => {
                          const params = convertParamsToQueryParams(annotation);
                          const url = DASHBOARD_URLS[annotation.dashboard];
                          dispatch(setParameters(params));
                          history.push(url);
                        }}
                      />
                    </TableCell>
                    <TableCell className={`${classes.tableCell}`} align="right">
                      <img
                        src={DeleteIcon}
                        alt="Delete Icon"
                        className={classes.deleteIcon}
                        onClick={() => {
                          setSelectedAnnotation(annotation);
                          setDeleteDialogOpen(true);
                        }}
                      />
                    </TableCell>
                  </StyledTableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          className={classes.tablePagination}
          rowsPerPageOptions={[]}
          component="div"
          count={annotations.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          backIconButtonProps={{
            style: {
              color: textColor,
            },
          }}
          nextIconButtonProps={{
            style: {
              color: textColor,
            },
          }}
          labelDisplayedRows={({ from, to, count }) => `Showing ${from} to ${to} of ${count} entries`}
        />
      </Box>
    </Sentry.ErrorBoundary>
  );
};

export default AnnotationsTable;
